import React, { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { InputText, Button } from '../components'
import { trpc } from '../trpc'
import { mapAuthError } from '../lib/authErrors'
import { LOGIN_PATH } from '../lib/routes'
import styles from './ResetPasswordPage.module.scss'

export const ResetPasswordPage = (): React.ReactElement => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // The token is a single-use credential that arrives in the query string.
  // Capture it once, then take it out of the URL: left there it goes into the
  // browser history, into the Referer header of anything this page loads, and
  // into any screenshot of the screen.
  const [token] = useState(() => searchParams.get('token') ?? '')
  useEffect(() => {
    if (searchParams.has('token')) setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const mutation = trpc.auth.resetPassword.useMutation({
    onSuccess: () => {
      setDone(true)
      setTimeout(() => navigate(LOGIN_PATH, { replace: true }), 2500)
    },
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setValidationError(null)

    if (newPassword.length < 8) {
      setValidationError('La contraseña debe tener al menos 8 caracteres.')
      return
    }
    if (newPassword !== confirm) {
      setValidationError('Las contraseñas no coinciden.')
      return
    }
    if (!token) {
      setValidationError('Token inválido o expirado. Solicita un nuevo enlace.')
      return
    }

    mutation.mutate({ token, newPassword })
  }

  if (done) {
    return (
      <div className={`row-flx flx-a-c flx-j-c p-24 bg-light-cream ${styles['reset-password-page']}`}>
        <div className={`p-v-40 p-h-32 border-s bg-white full-width ${styles['reset-password-page__card']}`}>
          <p className={`txt-a-c txt-light-kale ${styles['reset-password-page__info']}`}>
            Contraseña restablecida correctamente. Redirigiendo al login…
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={`row-flx flx-a-c flx-j-c p-24 bg-light-cream ${styles['reset-password-page']}`}>
      <div className={`p-v-40 p-h-32 border-s bg-white full-width ${styles['reset-password-page__card']}`}>
        <h1 className={`m-b-16 txt-kale ${styles['reset-password-page__heading']}`}>Nueva contraseña</h1>

        <form onSubmit={handleSubmit} className="col-flx flx-gap-16" noValidate>
          <InputText
            label="Nueva contraseña"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            required
          />

          <InputText
            label="Repetir contraseña"
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
          />

          {(validationError != null || mutation.isError) && (
            <p role="alert" className={`m-0 txt-grana ${styles['reset-password-page__error']}`}>
              {validationError ?? mapAuthError(mutation.error, 'reset')}
            </p>
          )}

          <Button
            type="submit"
            variant="pill"
            label={mutation.isPending ? 'Guardando…' : 'Guardar contraseña'}
            bgColor="olive"
            txtColor="white"
            disabled={mutation.isPending}
            className="full-width"
          />

          <div className="txt-a-c m-t-8">
            {/* replace: going back must not land on the entry this page just
                scrubbed the token out of. */}
            <Link to={LOGIN_PATH} replace className={`txt-olive txt-underline ${styles['reset-password-page__back-link']}`}>
              Volver al login
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
